/**
 * De HR-stap op het scherm (Story 7.2, AC-6/AC-7/AC-11)
 *
 * Drie kleine bouwstenen rond één gedeelde afleiding: de chip *Wacht op HR*
 * voor de goedkeuringslijst, de knoptekst van de leidinggevende, en de
 * statusregel die de medewerker in "Mijn verlof" ziet.
 *
 * WAAROM DIT EEN EIGEN BESTAND IS. De route bepaalt wélke tekst er staat, en
 * de route volgt uit `hr_stap_reden`. Dat gegeven hoort op precies één plek
 * per scherm te wonen (KE-11); de pagina's zelf combineren nergens zelf een
 * soort of een reden met een status om te bepalen wie aan zet is — ze geven
 * de rij door en lezen het antwoord.
 *
 * ALLE TEKSTEN KOMEN LETTERLIJK UIT §10.5 EN §10.8 VAN DE PRD. Er is er hier
 * geen bedacht en geen samengesteld (KE-7). Het onderscheid tussen de twee
 * hangt aan de ROUTE en niet aan de verlofsoort: de soort mag niet uit een
 * schermtekst af te leiden zijn door iemand die hem niet hoort te weten
 * (§8.3, KE-9).
 */

import {
  beslistAlleenHr,
  hrStapRedenLabel,
  isBijzonderVerlof,
  valtOnderNieuweVerlofregels,
  type ApprovalQueueItem,
  type GoedkeuringsstandRij,
  type LigtBijWaarde,
} from '@functieloonhuis/shared';
import { formatNlUren } from './urenFormat';
import { LEIDINGGEVENDE_LANGS_HR } from './bijzonderVerlofTeksten';

/**
 * Story 8.2, AC-9 — de rijen die dit bestand leest, dragen de stand die de
 * SERVER heeft afgeleid. Ze staan hier als eigen type omdat het scherm sinds
 * deze story niets meer zelf afleidt: het leest `ligtBij` en `stand` en
 * gebruikt de drie brongegevens alleen nog als onderbouwing.
 *
 * ONTBREKEN IS GEEN STAND. Er is geen terugval: staat het veld er niet, dan is
 * dat een fout in het serverantwoord en geen aanleiding om te gokken.
 */
export interface StandRij extends GoedkeuringsstandRij {
  ligtBij?: LigtBijWaarde | null;
  stand?: string | null;
}

/**
 * Beslist de leidinggevende NIET over deze soort? Doorgegeven aan het GEDEELDE
 * predicaat `beslistAlleenHr` (`packages/shared`) — hier staat geen eigen
 * lezing van de tabel meer, en nooit een vergelijking op een soortnaam (KE-9,
 * AD-27).
 *
 * WAAROM DIT HIER STAAT EN NIET `goedkeuringsroute` IS. Sinds story 8.2 roept
 * geen enkele schermregel de gedeelde route- of standafleiding meer aan (AC-9);
 * de server stuurt de stand mee. WELKE van de twee vastgestelde zinnen bij een
 * aanvraag hoort hangt echter aan de soort, en daar bestaat geen serverveld
 * voor. Dit is dus geen tweede afleiding van de stand.
 *
 * WAAROM HET NIET LANGER EEN EIGEN AFLEIDING IS (code review 2026-08-17). Deze
 * functie schreef `VERLOFSOORTEN[...].leidinggevendeBeslist` regel voor regel
 * na — dezelfde vraag als `goedkeuringsroute` en `ALLEEN_HR_REDENEN`, alleen
 * anders genoemd, waardoor de greptest van AC-9 er overheen gleed. De naam
 * blijft, want die zegt wat het scherm hier wil weten; het antwoord komt uit
 * één plek.
 *
 * Een reden die géén soort is (*boven de maandruimte*) staat niet in de tabel:
 * daar beslist de leidinggevende gewoon als eerste.
 */
export function leidinggevendeBeslistNiet(redenOfSoort: string | null | undefined): boolean {
  return beslistAlleenHr(redenOfSoort);
}

/** §10.5 — de leidinggevende was akkoord, HR is nog aan zet. */
const REGEL_NA_GOEDKEURING = 'Goedgekeurd door je leidinggevende — HR kijkt er nog naar';
const UITLEG_NA_GOEDKEURING =
  'Je bent die dag(en) vrij; je collega’s zien het al staan. Pas als HR akkoord is, gaat het naar de loonadministratie.';

/** §10.8 — route `alleen_hr`: er ging geen goedkeuring van de leidinggevende aan vooraf. */
const REGEL_ALLEEN_HR = 'Ingediend — HR kijkt ernaar';
const UITLEG_ALLEEN_HR =
  'Je bent die tijd vrij; je collega’s zien het al staan. HR bekijkt je aanvraag nog.';

/** §10.5 — de chiptekst zelf. */
const CHIP_LABEL = 'Wacht op HR';

// ── Story 11.1/11.2 — de HR-stap op een URENWIJZIGING (§10 aangevuld) ──
//
// De zes zinnen hieronder zijn op 2026-08-18 door de opdrachtgever
// vastgesteld en zijn bindend onder KE-7: ze worden overgenomen zoals ze er
// staan en nergens onderweg geherformuleerd. Het waren er eerst vijf; de zesde
// vult het venster tussen de goedkeuring van de leidinggevende en het besluit
// van HR, dat tot dan toe stil was. Ze wonen hier, bij de rest van de
// HR-stap-teksten, en niet in een paginabestand — dan zou dezelfde zin op twee
// schermen in twee spellingen kunnen belanden.

/**
 * Wat de INDIENER ziet op route `alleen_hr` — calamiteiten- en zorgverlof.
 * Daar beslist de leidinggevende inderdaad niet mee, en dan klopt de zin.
 *
 * *"Deze wijziging"* en niet *"Je wijziging"*: de indiener van een correctie
 * kan de leidinggevende of HR zelf zijn (AD-24), niet alleen de medewerker.
 */
const MELDING_WIJZIGING_NAAR_HR =
  'Deze wijziging gaat naar HR. Je leidinggevende beoordeelt hem niet.';

/**
 * Wat diezelfde indiener leest op route `leidinggevende_dan_hr` — boven de
 * maandruimte en bijzonder verlof.
 *
 * WAAROM ER TWEE ZIJN (besluit van de opdrachtgever, 2026-08-18). De zin
 * hierboven was aanvankelijk als ENIGE vastgesteld, en hij is onwaar zodra de
 * leidinggevende juist wél als eerste beslist. Op deze route is dat precies
 * wat er gebeurt: hij beoordeelt, en HR kijkt er daarna nog naar.
 *
 * De vorm spiegelt bewust de bestaande §10.5-regel voor de leidinggevende
 * (*"Keur je goed, dan beoordeelt HR het daarna."*), zodat de twee partijen
 * dezelfde volgorde in dezelfde woorden lezen.
 */
const MELDING_WIJZIGING_LEIDINGGEVENDE_EERST =
  'Je leidinggevende beoordeelt deze wijziging eerst. Keurt hij goed, dan kijkt HR er daarna nog naar.';

/**
 * En wat diezelfde indiener leest in het venster DAARNA: de leidinggevende
 * keurde goed, HR moet nog (vastgesteld door de opdrachtgever, 2026-08-18).
 *
 * Deze zin bestaat alleen op route `leidinggevende_dan_hr`. Op `alleen_hr` kan
 * hij per definitie niet verschijnen — daar beslist de leidinggevende niet, dus
 * is er geen goedkeuring van hem om te melden.
 *
 * DE VORM SPIEGELT {@link REGEL_NA_GOEDKEURING}, de al vastgestelde §10.5-regel
 * voor een AANVRAAG in precies dezelfde situatie: zelfde opbouw, zelfde
 * gedachtestreepje, zelfde tweede helft. Wat eraan toegevoegd is, is *deze
 * wijziging* — zonder die woorden leest de zin als een uitspraak over de
 * aanvraag eronder, en dat is precies het onderscheid dat de twee zinnen
 * hierboven ook nodig hadden.
 */
const MELDING_WIJZIGING_NA_GOEDKEURING =
  'Je leidinggevende is akkoord met deze wijziging — HR kijkt er nog naar';

/** Wat HR bij zo'n wijziging in zijn beoordeellijst leest. */
const WIJZIGING_BOVEN_MAANDRUIMTE_HR =
  'Wijziging boven de maandruimte — beoordeel of dit een uitzondering is.';

/** §10.7 — de zin bij verlof van vóór de nieuwe regels, letterlijk (KE-7). */
const REGEL_VAN_VOOR_DE_NIEUWE_REGELS =
  'Deze aanvraag is van vóór de nieuwe regels en heeft geen soort.';

/**
 * Story 5.4: de verlofgroep waar een soort bij hoort — Nmbrs-type 3,
 * *Kort verzuim + Bijz.verlof* (FR-1). Bij de andere drie hoort er sowieso
 * geen soort, dus valt er ook niets uit te leggen.
 */
const VERLOFGROEP_KORT_VERZUIM = 3;

/**
 * AC-7 — *Wacht op HR*, in de verlof-familie (teal) en nooit rood: rood
 * blijft voorbehouden aan fout en afwijzing (UX-DR11, FR-23).
 *
 * AC-11: de betekenis staat als tekst en niet alleen in kleur; het is een
 * `<span>` en geen knop, dus hij voegt niets toe aan de tab-volgorde en kan
 * geen focusval worden.
 */
export function WachtOpHrChip() {
  return (
    <span
      data-testid="wacht-op-hr-chip"
      className="inline-block px-2 py-0.5 rounded-full text-xs font-medium border bg-[#54949E]/10 text-[#54949E] border-[#54949E]/30"
    >
      {CHIP_LABEL}
    </span>
  );
}

/**
 * AC-7 — de knoptekst van de leidinggevende. Bij een aanvraag met een
 * HR-stap zegt de knop wat er werkelijk gebeurt: goedkeuren stuurt hem door
 * in plaats van hem naar de loonadministratie te zetten.
 */
export function goedkeurKnopTekst(rij: GoedkeuringsstandRij): string {
  // Story 8.2, AC-9: een kale veldlezing. Draagt de rij een reden voor de
  // HR-stap, dan stuurt goedkeuren hem door in plaats van hem te boeken; geen
  // reden betekent dat er geen HR-stap is (FR-19). Dat is dezelfde uitkomst als
  // voorheen, zonder de afleiding in het scherm.
  return rij.hrStapReden == null ? 'Goedkeuren' : 'Goedkeuren en doorsturen naar HR';
}

/**
 * Story 10.3, AC-5 (§10.5) — wat de leidinggevende leest vóórdat hij als eerste
 * goedkeurt: dat zijn goedkeuring de aanvraag doorstuurt in plaats van hem af
 * te ronden. De knoptekst zei dat al ({@link goedkeurKnopTekst}); deze zin zegt
 * er waaróm bij.
 *
 * UIT DE ROUTE EN NIET UIT DE SOORTNAAM (KE-11, AD-29). `hrStapReden` is het
 * veld waar de hele route aan hangt en de server heeft het al bepaald; het
 * vergelijken zelf gebeurt met het gedeelde predicaat, zodat er in dit bestand
 * geen soortnaam staat (KE-9). Een aanvraag zonder HR-stap draagt deze zin
 * niet, ook niet wanneer de soort er wél op staat.
 *
 * ALLEEN VOOR DE LEIDINGGEVENDE. HR is zélf de tweede stap en heeft niets aan
 * de mededeling dat het nog langs HR gaat; wie de HR-kant ziet krijgt hem dus
 * niet. Deze component kent de STAND van de aanvraag niet en mag die ook niet
 * kennen (N2, story 7.1): de reden en de stand blijven uit elkaar.
 */
export function LeidinggevendeDoorstuurRegel({
  rij,
  zietDeHrKant,
}: {
  rij: Pick<ApprovalQueueItem, 'hrStapReden' | 'soort'>;
  zietDeHrKant: boolean;
}) {
  if (zietDeHrKant) return null;
  if (rij.soort === 'urenwijziging') return null;
  if (!isBijzonderVerlof(rij.hrStapReden)) return null;

  return (
    <p data-testid="leidinggevende-langs-hr" className="mt-2 text-xs text-gray-600">
      {LEIDINGGEVENDE_LANGS_HR}
    </p>
  );
}

// ── Story 7.3: het HR-besluit op het scherm ──

/** §10.5 — de twee varianten van de goedkeurknop van HR. */
const KNOP_GOEDKEUREN_EN_BOEKEN = 'Goedkeuren en boeken';
const KNOP_GOEDKEUREN = 'Goedkeuren';

/** §10.5 — de bevestiging na goedkeuring van een soort die wél boekt. */
const BEVESTIGING_GEBOEKT = 'Goedgekeurd. Het verlof is naar de loonadministratie gestuurd.';
/** §10.8 — en van een soort die nooit boekt, met de uitleg eronder (FR-23). */
const BEVESTIGING_NIET_GEBOEKT = 'Goedgekeurd. Hiermee is de aanvraag afgerond.';
const UITLEG_NIET_GEBOEKT =
  'Dit verlof gaat niet naar de loonadministratie. Verwerk het loon zelf.';

/** §10.6 — wat er bij het redenveld staat wanneer HR afwijst (FR-18, KE-3). */
export const REDEN_UITLEG_HR =
  'Vul in waarom je dit afwijst. Je medewerker en zijn leidinggevende zien deze reden.';

/**
 * Story 7.3/8.2 — bij wie een rij ligt: een KALE VELDLEZING.
 *
 * De server leidt dat af met `goedkeuringsstand` en stuurt het antwoord mee
 * (story 8.2, AC-1). Er is geen terugval meer die het scherm alsnog zelf laat
 * rekenen (AC-9): ontbreekt het veld, dan is dat een fout in het antwoord en
 * geen aanleiding om te gokken. `null` betekent hier dus *onbekend*, nooit
 * *ligt bij niemand*.
 */
export function ligtBijVan(
  rij: GoedkeuringsstandRij & { ligtBij?: LigtBijWaarde | null }
): LigtBijWaarde | null {
  return rij.ligtBij ?? null;
}

/**
 * AC-5 — bereikt dit verlof de loonadministratie na goedkeuring?
 *
 * De SERVER leidt dit af uit `VERLOFSOORTEN` (AD-27) en stuurt het mee. Het
 * scherm leest dat veld en raadpleegt de soort niet meer (story 8.2, AC-9): de
 * terugval op `soortBoektNooit` wás de vierde formulering van *boekt deze
 * soort?* en is precies wat KE-11 verbiedt.
 *
 * ONTBREEKT HET VELD, DAN HANGT HET ANTWOORD NIET AAN DE SOORT. Er wordt niet
 * per soort teruggerekend — dat zou de terugval zijn die AC-9 wegneemt. Wat er
 * dan overblijft is de regel voor verlof waarover niets bekend is, en die is
 * dezelfde als voor verlof zonder soort: gewoon verlof boekt (FR-19).
 */
export function boektNaGoedkeuring(item: ApprovalQueueItem): boolean {
  if (typeof item.boektInLoonadministratie === 'boolean') return item.boektInLoonadministratie;
  return true;
}

/** AC-5 — de knoptekst van HR: hij zegt of er ook geboekt wordt. */
export function hrGoedkeurKnopTekst(boekt: boolean): string {
  return boekt ? KNOP_GOEDKEUREN_EN_BOEKEN : KNOP_GOEDKEUREN;
}

/**
 * AC-2 — de uitsplitsing van de teller boven de lijst: één deel per reden die
 * daadwerkelijk voorkomt, in de volgorde waarin ze in de lijst staan (§10.5:
 * *"1 boven de 3 uur, 2 bijzonder verlof, 1 calamiteiten- of zorgverlof"*).
 *
 * Een reden met nul aanvragen komt er nooit in — "0 bijzonder verlof" is geen
 * informatie, alleen ruis. De woorden komen uit de gedeelde afleiding en niet
 * uit een vergelijking op een soortnaam (KE-9, KE-11), en het tellen zelf
 * staat hier zodat de pagina de reden van de HR-stap nergens hoeft te
 * combineren met een status (N2 bij story 7.1).
 */
export function hrTellerDelen(bijHr: Pick<ApprovalQueueItem, 'hrStapReden'>[]): string[] {
  // Een `Map` bewaart de volgorde van eerste voorkomen.
  const perReden = new Map<string, number>();
  for (const rij of bijHr) {
    const reden = rij.hrStapReden;
    if (reden == null) continue;
    perReden.set(reden, (perReden.get(reden) ?? 0) + 1);
  }
  return [...perReden].map(
    ([reden, aantal]) => `${aantal} ${(hrStapRedenLabel(reden) ?? reden).toLowerCase()}`
  );
}

/**
 * AC-5 — de bevestiging ná dat besluit, met de uitlegregel waar die hoort.
 *
 * `null` IS EEN ANTWOORD, EN HET IS *ZWIJGEN* (code review 2026-08-17). Waar de
 * knoptekst bij een ontbrekend serverveld nog een verdedigbare standaard heeft,
 * heeft deze zin die niet: allebei de varianten doen een harde mededeling over
 * de loonadministratie. Valt `boektInLoonadministratie` weg op een rij die nooit
 * boekt, dan zou de bovenste variant zeggen dat het verlof verstuurd is terwijl
 * er niets geboekt is. De chip en de statusregel zwijgen bij een ontbrekend
 * serverveld al; deze doet dat nu ook.
 */
export function hrBesluitBevestiging(
  boekt: boolean | null | undefined
): { regel: string; uitleg: string | null } | null {
  if (typeof boekt !== 'boolean') return null;
  return boekt
    ? { regel: BEVESTIGING_GEBOEKT, uitleg: null }
    : { regel: BEVESTIGING_NIET_GEBOEKT, uitleg: UITLEG_NIET_GEBOEKT };
}

/**
 * AC-1 — de chip die zegt WAAROM deze aanvraag bij HR ligt (§10.5).
 *
 * De woorden komen uit de gedeelde afleiding (`hrStapRedenLabel`) en niet uit
 * een vergelijking op een soortnaam in dit scherm (KE-9, KE-11). Rendert
 * niets zonder reden — een aanvraag zonder HR-stap heeft hier niets te
 * melden.
 *
 * AC-13: de betekenis staat als TEKST, de kleur is versiering. Teal, de
 * verlof-familie; rood blijft van fout en afwijzing (Consistency Conventions).
 *
 * ALLEEN ZOLANG ER NOG NIETS BESLOTEN IS — maar die voorwaarde staat NIET hier
 * (code review 2026-08-17). De reden blijft na een besluit gewoon op de rij
 * staan; zij legt vast waaróm de aanvraag bij HR lag en zegt niets over de
 * afloop. Een afgewezen aanvraag hoort deze chip dus niet meer te dragen.
 *
 * De toets zit op de AANROEPPLEK in `ApprovalQueuePage`, en dat is geen
 * detail: N2 (story 7.1) verbiedt dat één bestand de reden en de stand van een
 * aanvraag combineert, want dat is de vorm waarin een tweede afleiding van de
 * goedkeuringsroute ontstaat naast `goedkeuringsstand`. De pagina kent de
 * stand en kent géén reden; dit bestand kent de reden en kent géén stand. Die
 * scheiding blijft hier staan.
 */
export function HrStapRedenChip({ rij }: { rij: Pick<ApprovalQueueItem, 'hrStapReden'> }) {
  const label = hrStapRedenLabel(rij.hrStapReden);
  if (label === null) return null;

  return (
    <span
      data-testid="hr-stap-reden-chip"
      className="inline-block px-2 py-0.5 rounded-full text-xs font-medium border bg-[#54949E]/10 text-[#54949E] border-[#54949E]/30"
    >
      {label}
    </span>
  );
}

// ── Story 6.3: de overschrijding in de goedkeuringslijst (FR-6, §10.5) ──

/**
 * §10.5, woord voor woord (KE-7). Alleen de naam, het getal en de maandnaam
 * worden ingevuld; de zin zelf verandert niet.
 *
 * Het getal komt uit de stand die bij het INDIENEN is vastgelegd en wordt hier
 * niet opnieuw uitgerekend — de goedkeuringslijst is geen aanroepplek van de
 * teller (AD-34). Maximaal twee decimalen, in Nederlandse notatie: `4,25` en
 * niet `4,3`.
 *
 * De *drie uur* staat er als deel van de ZIN en niet als ingevulde waarde: §10.5
 * schrijft alleen de naam, het getal en de maandnaam voor als variabel. Wordt de
 * maandruimte ooit een instelling, dan verandert deze zin mee in §10 en niet
 * hier ter plekke.
 */
export function maandruimteWaarschuwingZin(
  naam: string,
  stand: { gebruikt: number; maand: string },
  opties: { vraagtEenEersteBesluit?: boolean } = {}
): string {
  const standZin =
    `${naam} komt hiermee op ${formatNlUren(stand.gebruikt)} uur medisch bezoek in ${stand.maand}, ` +
    'boven de 3 uur uit het handboek.';
  // Story 11.1, AC-10 (code review 2026-08-18): de tweede zin is een
  // AANWIJZING VOOR DE LEIDINGGEVENDE die nog moet beslissen. Bij een
  // urenwijziging die al bij HR ligt is er voor hem niets te keuren, en dan
  // leest HR een opdracht die niet voor hem geldt. De vastgestelde tekst voor
  // dat geval is precies de eerste zin en niets erachter — hij wordt hier dus
  // weggelaten en niet vervangen door een bedachte variant (KE-7).
  if (opties.vraagtEenEersteBesluit === false) return standZin;
  return `${standZin} Keur je goed, dan beoordeelt HR het daarna.`;
}

/**
 * AC-2 — de waarschuwingsregel bij een aanvraag die over de maandruimte gaat.
 *
 * DRIE VOORWAARDEN, EN GEEN ENKELE VERGELIJKING OP EEN SOORTNAAM OF EEN REDEN
 * (KE-9, KE-11):
 *
 * 1. er ís een HR-stap — de server heeft dat vastgesteld, het scherm niet;
 * 2. er is een stand vastgelegd bij het indienen;
 * 3. die stand gaat daadwerkelijk over zijn eigen maximum heen.
 *
 * De derde voorwaarde staat er omdat de zin anders zichzelf kan tegenspreken:
 * een aanvraag die pas bij het GOEDKEUREN boven de grens uitkomt draagt een
 * stand ónder de drie uur, en *"komt hiermee op 2,5 uur …, boven de 3 uur"*
 * is geen zin die iemand hoort te lezen. §10.5 kent voor dat geval geen tekst,
 * en er wordt er hier geen bedacht (KE-7) — dan verschijnt er niets.
 *
 * AC-11: betekenis als TEKST, geen waarschuwingsvorm. Dit is geen fout; rood
 * blijft voorbehouden aan fout en afwijzing (Consistency Conventions).
 *
 * De zin eindigt op *"Keur je goed, dan beoordeelt HR het daarna"* — een
 * aanwijzing voor een besluit dat nog genomen moet worden. Op een aanvraag
 * waarover al beslist is hoort hij dus niet meer te staan, en die vierde
 * voorwaarde staat op de aanroepplek in `ApprovalQueuePage`, om dezelfde reden
 * als bij {@link HrStapRedenChip} hierboven: N2 houdt de reden en de stand uit
 * elkaar (code review 2026-08-17).
 */
/**
 * Story 11.1, AC-9/AC-10 — wat HR bij een URENWIJZIGING leest die over de
 * maandruimte tilt (§10, aangevuld op 2026-08-18).
 *
 * Alleen bij HR: de leidinggevende krijgt niet de vraag of dit een
 * uitzondering is, want dat is niet zijn beslissing. Alleen bij de reden die
 * de zin ook noemt — voor de twee soortredenen kent §10 geen tekst, en er
 * wordt er hier geen bedacht (KE-7).
 */
export function UrenwijzigingBovenMaandruimteRegel({
  item,
  zietDeHrKant,
}: {
  item: Pick<ApprovalQueueItem, 'soort' | 'hrStapReden'>;
  zietDeHrKant: boolean;
}) {
  if (!zietDeHrKant) return null;
  // Deze regel gaat uitsluitend over een urenwijziging; bij een aanvraag draagt
  // §10.5 zijn eigen waarschuwing (`MaandruimteWaarschuwing` hieronder).
  if (item.soort !== 'urenwijziging') return null;
  if (item.hrStapReden !== 'boven_maandruimte') return null;

  return (
    <p data-testid="urenwijziging-boven-maandruimte" className="mt-2 text-xs text-[#2F5A7A]">
      {WIJZIGING_BOVEN_MAANDRUIMTE_HR}
    </p>
  );
}

/**
 * Story 11.1/11.2 — wat de INDIENER van een urenwijziging leest zolang er nog
 * een besluit over openstaat.
 *
 * WELKE van de twee vastgestelde zinnen erbij hoort hangt aan de ROUTE en niet
 * aan de verlofsoort (KE-9, KE-11): beslist de leidinggevende niet mee, dan
 * gaat de wijziging naar HR en beoordeelt hij hem niet; beslist hij juist
 * eerst, dan zegt de zin dát, met HR erachter. Zonder HR-stap heeft deze regel
 * niets te melden.
 *
 * EN WANNEER (code review 2026-08-18). De zinnen zijn niet op hetzelfde moment
 * waar, en dat is de reden dat deze regel niet alleen naar de reden kijkt:
 *
 * - de `alleen_hr`-zin geldt VANAF HET INDIENEN — er komt geen eerste besluit,
 *   dus er verandert niets aan wat hij zegt;
 * - de aankondigende zin geldt tot het eerste besluit: hij kondigt een besluit
 *   AAN dat nog genomen moet worden;
 * - daarna, zolang HR nog moet kijken, staat de derde zin er (vastgesteld op
 *   2026-08-18). Het venster stond eerder leeg omdat §10 er geen tekst voor
 *   kende en er hier geen bedacht wordt (KE-7); die tekst is er nu.
 *
 * WAAROM `correctieDecidedAt != null` HIER "DE LEIDINGGEVENDE KEURDE GOED"
 * BETEKENT EN NOOIT "HIJ WEES AF". Een afwijzing rondt de wijziging af: de
 * correctierij krijgt dan status `afgewezen` en is geen OPENSTAANDE correctie
 * meer. Beide aanroepplekken lezen uitsluitend openstaande correcties — *Mijn
 * verlof* laat `correctieHrStapReden` dan leeg, en de goedkeuringslijst draagt
 * de rij niet meer. Een afgewezen wijziging bereikt deze regel dus niet, en om
 * dezelfde reden een door HR afgehandelde ook niet: ook dat besluit zet een
 * eindstand. Het venster ertussen is het enige dat overblijft.
 *
 * `correctieDecidedAt` is het besluitmoment van de CORRECTIE — de rij waar
 * deze regel over gaat — en nooit dat van de aanvraag eronder.
 */
export function UrenwijzigingIndienerRegel({
  correctieHrStapReden,
  correctieDecidedAt,
}: {
  correctieHrStapReden: string | null | undefined;
  correctieDecidedAt?: string | null;
}) {
  if (correctieHrStapReden == null) return null;

  const alleenHr = leidinggevendeBeslistNiet(correctieHrStapReden);
  // Op `alleen_hr` bestaat dit venster niet: daar is geen goedkeuring van de
  // leidinggevende om te melden, wat er ook in `correctieDecidedAt` staat.
  const naGoedkeuringDoorDeLeidinggevende = !alleenHr && correctieDecidedAt != null;

  return (
    <p data-testid="urenwijziging-indiener-regel" className="mt-1 text-xs text-[#2F5A7A]">
      {alleenHr
        ? MELDING_WIJZIGING_NAAR_HR
        : naGoedkeuringDoorDeLeidinggevende
          ? MELDING_WIJZIGING_NA_GOEDKEURING
          : MELDING_WIJZIGING_LEIDINGGEVENDE_EERST}
    </p>
  );
}

/**
 * Dezelfde regel, maar dan op de GOEDKEURINGSLIJST — voor een correctie die de
 * leidinggevende of HR zélf indiende (AD-24, code review 2026-08-18).
 *
 * WAAROM DIT EEN EIGEN COMPONENT IS EN GEEN TWEE PROPS IN DE PAGINA. N2 (story
 * 7.1) verbiedt dat een paginabestand de reden van een rij combineert met haar
 * stand; `ApprovalQueuePage` kent daarom de stand en géén reden, en dit bestand
 * kent de reden en géén stand. Zou de pagina hier `item.hrStapReden`
 * doorgeven, dan stond die combinatie er alsnog. Ze geeft de rij door en leest
 * het antwoord — precies zoals bij {@link UrenwijzigingBovenMaandruimteRegel}.
 *
 * Op een urenwijziging dragen `hrStapReden` en `decidedAt` die van de
 * CORRECTIE; de server zet ze daar neer (story 11.1, AC-9).
 */
export function UrenwijzigingIndienerRegelOpDeLijst({
  item,
  currentUserId,
}: {
  item: Pick<ApprovalQueueItem, 'soort' | 'hrStapReden' | 'decidedAt' | 'correctie'>;
  currentUserId: number | undefined;
}) {
  if (item.soort !== 'urenwijziging') return null;
  if (currentUserId === undefined) return null;
  if (item.correctie?.submittedBy !== currentUserId) return null;

  return (
    <UrenwijzigingIndienerRegel
      correctieHrStapReden={item.hrStapReden}
      correctieDecidedAt={item.decidedAt}
    />
  );
}

export function MaandruimteWaarschuwing({ item }: { item: ApprovalQueueItem }) {
  const stand = item.maandtellerBijIndienen ?? null;
  if (item.hrStapReden == null || stand === null) return null;
  if (stand.gebruikt <= stand.maximum) return null;

  return (
    <p
      data-testid="maandruimte-waarschuwing"
      className="mt-2 text-xs text-[#2F5A7A] border-t border-[#E2E8F0] pt-2"
    >
      {maandruimteWaarschuwingZin(item.applicantName, stand, {
        // Bij een urenwijziging vraagt deze regel geen eerste besluit — die
        // rij ligt bij HR, en de vastgestelde tekst eindigt bij het getal.
        vraagtEenEersteBesluit: item.soort !== 'urenwijziging',
      })}
    </p>
  );
}

/** De velden die {@link GeenSoortRegel} van een rij nodig heeft. */
export interface GeenSoortRij {
  leaveType: number;
  startDate: string;
  verlofsoort?: string | null;
}

/**
 * Story 5.4, AC-7 — bij een aanvraag zonder soort staat er NIETS waar de soort
 * zou staan: geen streepje, geen *onbekend*, geen grijze placeholder. Alleen
 * daar waar het aan de orde is staat de vastgestelde zin uit §10.7, en die is
 * hier niet bedacht of samengesteld (KE-7).
 *
 * *Aan de orde* is precies één geval: een aanvraag in verlofgroep 3 zonder
 * soort, waarvan de startdatum vóór de ingangsdatum ligt. Alle andere gevallen
 * renderen niets, en daar zit de hele terughoudendheid van deze regel in:
 *
 * - **Staat de schakelaar uit** (`ingangsdatum === null`), dan zwijgt hij.
 *   Anders zou élke bestaande aanvraag deze zin dragen op een portal die zich
 *   verder exact gedraagt zoals vandaag (AC-5).
 * - **Bij de andere drie verlofgroepen** hoort geen soort, dus valt er niets
 *   te verklaren (FR-1) — ook niet bij verlof dat uit Nmbrs is teruggelezen.
 * - **Valt de aanvraag wél onder de nieuwe regels**, dan is een lege soort een
 *   ander verhaal en niet dat van deze story.
 *
 * De datumvergelijking komt uit `valtOnderNieuweVerlofregels`
 * (`packages/shared`) en wordt hier niet overgeschreven (KE-9, AC-4).
 */
export function GeenSoortRegel({
  rij,
  ingangsdatum,
}: {
  rij: GeenSoortRij;
  /** `null` zolang de instelling niet gezet of onleesbaar is (AC-2). */
  ingangsdatum: string | null | undefined;
}) {
  if (ingangsdatum == null) return null;
  if (rij.leaveType !== VERLOFGROEP_KORT_VERZUIM) return null;
  if (rij.verlofsoort) return null;
  if (valtOnderNieuweVerlofregels(rij.startDate, ingangsdatum)) return null;

  return (
    <p data-testid="geen-soort-regel" className="mt-1 text-xs text-gray-500">
      {REGEL_VAN_VOOR_DE_NIEUWE_REGELS}
    </p>
  );
}

// ── Story 8.1: de AFGERONDE stand voor de medewerker (AC-7, §10.8) ──

/** §10.8, woord voor woord (KE-7) — de stand nadat HR heeft goedgekeurd. */
const STAND_GOEDGEKEURD_DOOR_HR = 'Goedgekeurd door HR';

/** De twee velden waar {@link AfgerondDoorHrRegel} op leest. */
export interface AfgerondDoorHrRij {
  /** De stand zoals de SERVER hem heeft afgeleid (story 8.2, AC-10). */
  stand?: string | null;
  /** Het moment waarop HR besliste — leeg bij verlof zonder HR-stap. */
  hrDecidedAt?: string | Date | null;
}

/**
 * Story 8.1, AC-7 — *"Goedgekeurd door HR"* voor de medewerker.
 *
 * WAAROM DIT NAAST DE BADGE STAAT EN NIET ERIN. Het bestaande *Goedgekeurd*
 * blijft onveranderd staan bij verlof zonder HR-stap (KE-6). Een vijfde
 * badgelabel zou die twee door één afleiding laten lopen; een eigen regel laat
 * de badge met rust en zet de §10.8-tekst bij de andere teksten van deze soort.
 *
 * DE TWEE VOORWAARDEN, EN GEEN ENKELE VERGELIJKING OP EEN SOORTNAAM (KE-9):
 * de aanvraag is rond (`stand`, door de SERVER afgeleid — het scherm rekent
 * niet zelf, KE-11) én er is werkelijk een HR-besluit geweest
 * (`hr_decided_at`). Zonder die tweede voorwaarde zou élk goedgekeurd verlof
 * deze regel dragen; zonder de eerste ook een aanvraag die HR net heeft
 * afgewezen.
 *
 * AC-9: de betekenis staat als TEKST en niet in de kleur van een badge, en de
 * regel is leesbaar zonder die kleur te kennen.
 */
export function AfgerondDoorHrRegel({ rij }: { rij: AfgerondDoorHrRij }) {
  if (rij.stand !== 'goedgekeurd') return null;
  if (rij.hrDecidedAt == null) return null;

  return (
    <p data-testid="afgerond-door-hr-regel" className="mt-1 text-xs font-medium text-[#2F5A7A]">
      {STAND_GOEDGEKEURD_DOOR_HR}
    </p>
  );
}

/**
 * AC-6 — wat de medewerker ziet zolang HR nog moet kijken. Rendert niets
 * zodra de aanvraag ergens anders staat; een aanvraag zonder HR-stap of een
 * die al helemaal rond is heeft hier niets te melden.
 *
 * AC-11: de uitleg hangt via `aria-describedby` aan de regel waar hij over
 * gaat, zodat een schermlezer hem bij die aanvraag hoort en niet als losse
 * tekst tussen de rijen.
 */
export function HrStapStatusregel({
  rij,
  id,
}: {
  rij: StandRij;
  /** Uniek per rij — de koppeling tussen de regel en zijn uitleg. */
  id: number | string;
}) {
  // Story 8.2, AC-9/AC-10: de stand komt van de server, ook op *Mijn verlof* —
  // `getOwnLeaveOverview` draagt hem sinds AC-10. Geen terugval, geen tweede
  // afleiding: staat er iets anders dan *wacht op HR*, dan is er hier niets te
  // melden.
  if (rij.stand !== 'wacht_op_hr') return null;

  // WELKE van de twee vastgestelde zinnen erbij hoort, hangt aan de soort en
  // niet aan de stand — zie {@link leidinggevendeBeslistNiet}.
  const alleenHr = leidinggevendeBeslistNiet(rij.hrStapReden);
  const uitlegId = `hr-stap-uitleg-${id}`;

  return (
    <div data-testid="hr-stap-statusregel" className="mt-1">
      <span aria-describedby={uitlegId} className="block text-xs font-medium text-[#2F5A7A]">
        {alleenHr ? REGEL_ALLEEN_HR : REGEL_NA_GOEDKEURING}
      </span>
      <span id={uitlegId} className="block text-xs text-gray-500">
        {alleenHr ? UITLEG_ALLEEN_HR : UITLEG_NA_GOEDKEURING}
      </span>
    </div>
  );
}
